import { IkeId } from '@ikegps/core-shared';
import {
	action,
	computed,
	type IReactionDisposer,
	type IReactionOptions,
	type IReactionPublic,
	observable,
	reaction,
	runInAction,
	makeObservable,
} from 'mobx';

export type IkeInputStateSetter<T> = (val: T) => void;
export type IkeInputStateGetter<T> = () => T;

export interface IkeInputState {
	_type: string;
	id: string;

	isValid: boolean;
	isFilled: boolean;
	isRequired: boolean;
	isLabelShown: boolean;
	isAutoFocused: boolean;

	reset(): void;
	setDisabled(isDisabled: boolean): void;
	setAutoFocus(isAutoFocused: boolean): void;
	destroy(): void;
}

export enum InputStateTypes {
	NUMBER = 'number',
	PASSWORD = 'password',
	TEXT = 'text',
	COLOR = 'color',
}

export class InputState<T = string> implements IkeInputState {
	_type = 'inputstate';

	id: string;

	/** Current parsed value of the input */
	@observable _value: T | null = null;

	@computed get value(): T | null {
		return this._value;
	}

	/** Current value of the input displayed to the user (may be invalid) */
	@observable inputValue: string | null = null;

	/** Whether the input is currently focused */
	@observable focused = false;

	/** Whether the input text is currently selected */
	@observable selected = false;

	/** Whether the input has been touched */
	@observable touched = false;

	/** Error message for the input */
	@observable _error = '';

	@computed get error(): string {
		return this._error;
	}

	/** Whether the input is disabled */
	@observable disabled = false;

	/** Whether the input is read-only */
	@observable readOnly = false;

	/** Placeholder input text */
	@observable placeholder: string = '';

	/** Label of the input */
	label = '';

	/** Type of input eg 'password' */
	type?: InputStateTypes = undefined;

	/** Input name used for form submission. Also used by browsers for input suggestion. */
	name: string | null = null;

	@observable isRequired: boolean = false;
	private _isAutoFocused: boolean = false;
	public get isAutoFocused(): boolean {
		return this._isAutoFocused;
	}
	public set isAutoFocused(value: boolean) {
		this._isAutoFocused = value;
	}
	isLabelShown: boolean = true;

	/*isErrorShown is used to disable the standard error message display (at generic input level) in order to display it later im the code (usefull for composite input field, like Location which is made of 3 NumberInputField)*/
	isErrorShown: boolean = true;

	reactions: IReactionDisposer[] = [];

	constructor(label: string, id = IkeId.generate(), type?: InputStateTypes) {
		makeObservable(this);

		this.id = id || IkeId.generate();
		this.label = label;
		this.type = type;
	}

	@action setValue(val: T | null) {
		this._value = val;
		this.inputValue = this.formatInputValue(val);
	}

	@action setInputValue(val: string) {
		this.inputValue = val;
		this._value = this.parseInputValue(val);
	}

	protected formatInputValue(val: T | null): string | null {
		return val != null ? String(val) : null;
	}

	protected parseInputValue(val: string): T | null {
		return val as any;
	}

	@action reset() {
		this._value = null;
		this.inputValue = null;
		this._error = '';
		this.focused = false;
		this.touched = false;
		this.disabled = false;
	}

	@action setFocused(val: boolean) {
		this.focused = val;
	}

	@action setTouched() {
		this.touched = true;
	}

	@action setSelected(selected: boolean) {
		this.selected = selected;
	}

	@action setError(error: string | null) {
		this._error = error || '';
	}

	@action setDisabled(disabled: boolean) {
		this.disabled = disabled;
	}

	@action setAutoFocus(value: boolean) {
		this.isAutoFocused = value;
		this.focused = value;
	}

	@action setPlaceholder(placeholder: string) {
		this.placeholder = placeholder;
	}

	hasError(): boolean {
		return this.error != null && this.error.length > 0;
	}

	@computed get isFilled(): boolean {
		if (typeof this.value === 'string') {
			return this.value.trim().length > 0;
		}

		return this.value != null;
	}

	@computed get isValid() {
		if (this.isRequired && !this.isFilled) {
			return false;
		}

		return !this.hasError();
	}

	reaction<Tx>(
		expression: (r: IReactionPublic) => Tx,
		effect: (arg: Tx, prev: Tx | undefined, r: IReactionPublic) => void,
		opts?: IReactionOptions<Tx, boolean>
	) {
		this.reactions.push(reaction(expression, effect, opts));
	}

	onValue(cb: (v: T | null) => void) {
		this.reaction(
			() => this.value,
			(v) => runInAction(() => cb(v))
		);
	}

	/**
	 * This function is designed to be optionally over written by the input state that requires an onblur function
	 */
	onBlur(_e: FocusEvent) {
		return;
	}

	/**
	 * This function is designed to be optionally over written by the input state that requires an onInput function
	 */
	onInput(_e: InputEvent) {
		return;
	}

	destroy() {
		// Destroy any reactions
		this.reactions.map((c) => c());
	}
}
